import { parse } from '@nostr/tools/nip27'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { isReplyNoteEvent, getParents } from '@/lib/event'
import { tagNameEquals, pointerFromATag } from '@/lib/tag'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import {
  addEventToCache,
  addUsernameToIndex,
  fetchEvent,
  getSeenEventRelays,
  trackEventSeenOn
} from '@/services/client.service'
import { Event, verifyEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { npubEncode } from '@nostr/tools/nip19'
import { useEffect, useMemo, useState } from 'react'
import { useAsync } from 'react-use'
import { useTranslation } from 'react-i18next'
import Collapsible from './Collapsible'
import { FormattedTimestamp } from './FormattedTimestamp'
import UserAvatar from './UserAvatar'
import Username from './Username'
import SimpleUsername from './SimpleUsername'
import { useSecondaryPage } from '@/PageManager'
import { toNote, toGroupedProfile } from '@/lib/link'
import PinBuryBadge from './PinBuryBadge'
import CompactModeMenu from './CompactModeMenu'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowReloadHorizontalIcon } from '@hugeicons/core-free-icons'
import { truncateUrl } from '@/lib/url'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { isHex32 } from '@nostr/gadgets/utils'

const maxLength = 150

export default function CompactedEventCard({
  event,
  className,
  totalNotesInTimeframe,
  variant = 'note',
  filterMutedNotes = true,
  isSelected = false,
  onSelect,
  onLastNoteRead,
  onAllNotesRead,
  onMarkAsUnread,
  isLastNoteRead = false,
  areAllNotesRead = false
}: {
  event: Event
  className?: string
  totalNotesInTimeframe: number
  variant?: 'note' | 'repost'
  filterMutedNotes?: boolean
  isSelected?: boolean
  onSelect?: () => void
  onLastNoteRead?: () => void
  onAllNotesRead?: () => void
  onMarkAsUnread?: () => void
  isLastNoteRead?: boolean
  areAllNotesRead?: boolean
}) {
  const { push } = useSecondaryPage()
  const { t } = useTranslation()
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [targetEvent, setTargetEvent] = useState<Event | null>(null)

  const isRepost = variant === 'repost'
  const isReply = !isRepost && isReplyNoteEvent(event)

  const { value: repliedPubkey } = useAsync(async () => {
    if (!isReply) return undefined

    const pTags = event.tags.filter(
      (tag: string[]) => tag[0] === 'p' && !!tag[1] && tag[1] !== event.pubkey
    )
    if (pTags.length === 0) return undefined
    if (pTags.length === 1 && isHex32(pTags[0][1])) return pTags[0][1]

    const parents = getParents(event)
    if (parents.length === 0) return undefined
    const parent = parents[0]
    if (!('id' in parent) || !parent.id) return undefined

    try {
      const parentEvent = await fetchEvent({
        id: parent.id,
        relays: parent.relays ?? [],
        author: parent.author
      })
      return parentEvent?.pubkey
    } catch {
      return undefined
    }
  }, [event, isReply])
  const [previewText, setPreviewText] = useState<string | null>(null)

  // Generate preview text asynchronously
  useEffect(() => {
    // Reset preview when event changes
    setPreviewText(null)

    // For reposts, wait until targetEvent is loaded
    if (isRepost && !targetEvent) return

    const eventToPreview = isRepost && targetEvent ? targetEvent : event
    getPreviewText(eventToPreview).then(setPreviewText)
  }, [event, isRepost, targetEvent])

  // Repost logic - fetch target event for reposts
  useEffect(() => {
    if (!isRepost) return

    const fetch = async () => {
      try {
        const eventFromContent = event.content ? (JSON.parse(event.content) as Event) : null
        if (eventFromContent && verifyEvent(eventFromContent)) {
          if (
            eventFromContent.kind === kinds.Repost ||
            eventFromContent.kind === kinds.GenericRepost
          ) {
            return
          }
          addEventToCache(eventFromContent)
          const targetSeenOn = getSeenEventRelays(eventFromContent.id)
          if (targetSeenOn.length === 0) {
            const seenOn = getSeenEventRelays(event.id)
            for (const relay of seenOn) {
              trackEventSeenOn(eventFromContent.id, relay)
            }
          }
          setTargetEvent(eventFromContent)
          return
        }

        const [, id, relay, , pubkey] = event.tags.find(tagNameEquals('e')) ?? []
        if (id) {
          const targetEvent = await fetchEvent({
            id,
            relays: relay ? [relay] : [],
            author: pubkey
          })
          if (targetEvent) {
            setTargetEvent(targetEvent)
            return
          }
        }

        // generic reposts of replaceable/adressable events point at an a tag
        const pointer = event.tags.find(tagNameEquals('a'))
        const targetPointer = pointer ? pointerFromATag(pointer) : undefined
        if (targetPointer) {
          const targetEvent = await fetchEvent(targetPointer)
          if (targetEvent) setTargetEvent(targetEvent)
        }
      } catch {
        // ignore
      }
    }
    fetch()
  }, [event, isRepost])

  const shouldHide = useMemo(() => {
    if (!isRepost) return false
    if (!targetEvent) return true
    if (filterMutedNotes && mutePubkeySet.has(targetEvent.pubkey)) {
      return true
    }
    return false
  }, [isRepost, targetEvent, filterMutedNotes, mutePubkeySet])

  const showAllNotes = (e: React.MouseEvent) => {
    e.stopPropagation()
    onAllNotesRead?.()
    push(toGroupedProfile(event.pubkey))
  }

  const showLastNote = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect?.()
    if (!areAllNotesRead) {
      onLastNoteRead?.()
    }
    push(toNote(event))
  }

  if (shouldHide) return null

  return (
    <div className={className}>
      <Collapsible alwaysExpand={false}>
        <div
          className={`cursor-pointer hover:bg-muted/30 active:bg-muted/30 group/row transition-colors ${isSelected ? 'bg-muted/50' : ''}`}
          onClick={showLastNote}
        >
          {/* Top row */}
          <div className="pt-3 pb-1 cursor-pointer">
            <div className="px-4">
              <div className="flex justify-between items-center gap-2">
                <div
                  className={`flex items-center space-x-2 flex-1 ${!isSelected && isLastNoteRead && 'text-muted-foreground/70 grayscale'}`}
                >
                  <UserAvatar
                    user={event.pubkey}
                    size="normal"
                    className={!isSelected && isLastNoteRead ? 'opacity-50' : undefined}
                  />
                  <div className="flex-1 w-0">
                    <div className="flex gap-2 items-center">
                      <Username
                        user={event.pubkey}
                        className="font-semibold flex truncate cursor-pointer hover:text-primary"
                        skeletonClassName="h-4"
                      />
                      <PinBuryBadge pubkey={event.pubkey} />
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      {!isReply && !isRepost && (
                        <>
                          <FormattedTimestamp timestamp={event.created_at} className="shrink-0" />
                        </>
                      )}
                      {isReply && (
                        <>
                          <span className="shrink-0">{t('Replying to')}</span>
                          {repliedPubkey && (
                            <>
                              <UserAvatar
                                user={repliedPubkey}
                                size="xSmall"
                                className={!isSelected && isLastNoteRead ? 'opacity-50' : undefined}
                              />
                              <SimpleUsername user={repliedPubkey} className="line-clamp-1" />
                            </>
                          )}
                        </>
                      )}
                      {isRepost && targetEvent && (
                        <>
                          <span className="shrink-0 hidden md:block">Reposting</span>
                          <HugeiconsIcon
                            icon={ArrowReloadHorizontalIcon}
                            size={16}
                            className="shrink-0 block md:hidden"
                          />
                          <UserAvatar
                            user={targetEvent.pubkey}
                            size="xSmall"
                            className={!isSelected && isLastNoteRead ? 'opacity-50' : undefined}
                          />
                          <SimpleUsername user={targetEvent.pubkey} className="line-clamp-1" />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show compact mode menu and counter badge */}
                <div className="flex items-center gap-1">
                  <CompactModeMenu
                    pubkey={event.pubkey}
                    isLastNoteRead={isLastNoteRead}
                    areAllNotesRead={areAllNotesRead}
                    onMarkAsUnread={onMarkAsUnread}
                  />
                  <div
                    className={`w-8 h-8 ml-2 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer transition-all hover:border-primary/50 bg-primary/10 border border-primary/20
                      ${areAllNotesRead ? 'text-primary/50 grayscale' : 'text-primary'}`}
                    onClick={showAllNotes}
                  >
                    {totalNotesInTimeframe}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 pb-3 cursor-pointer pl-16" onClick={showLastNote}>
            {previewText === null ? (
              <Skeleton className="h-4 w-3/4" />
            ) : (
              <div
                className={`text-muted-foreground transition-colors line-clamp-2 break-keep wrap-break-word ${!isSelected && isLastNoteRead && 'text-muted-foreground/50 grayscale'}`}
              >
                {previewText}
              </div>
            )}
          </div>
        </div>
      </Collapsible>
      <Separator />
    </div>
  )
}

// Helper function to extract preview text from event
async function getPreviewText(event: Event): Promise<string> {
  if (event.kind === 20) {
    return '«image» ' + event.content.trim().substring(0, maxLength - 8)
  }

  let plainText = ''

  for (const block of parse(event.content)) {
    switch (block.type) {
      case 'text': {
        plainText += block.text
          .replace(/[*_~`#]/g, '') // Remove markdown characters
          .replace(/\n+/g, ' ') // Replace newlines with spaces
          .replace(/\s+/g, ' ') // Normalize whitespace
        break
      }
      case 'video': {
        plainText += '«video»'
        break
      }
      case 'audio': {
        plainText += '«audio»'
        break
      }
      case 'image': {
        plainText += '«image»'
        break
      }
      case 'reference': {
        if ('id' in block.pointer) {
          plainText += '«mention»'
        } else if ('identifier' in block.pointer) {
          plainText += '«mention»'
        } else {
          const profile = await loadNostrUser(block.pointer)
          if (profile) {
            addUsernameToIndex(profile)
            plainText += `@${profile.shortName}`
          } else {
            plainText += `@${npubEncode(block.pointer.pubkey).slice(0, 12)}...`
          }
        }
        plainText += ' '
        break
      }
      case 'relay': {
        plainText += block.url
        break
      }
      case 'url': {
        plainText += truncateUrl(block.url)
        break
      }
      case 'hashtag': {
        plainText += '#' + block.value + ' '
        break
      }
      case 'emoji': {
        plainText += ':' + block.shortcode + ':'
        break
      }
    }

    // Truncate if too long
    if (plainText.length > maxLength) {
      return plainText.trim().substring(0, maxLength).trim() + '...'
    }
  }

  return plainText.trim()
}
